import {
  setContextId,
  readContextIdFromResponse,
  ensureContextId,
} from "~/lib/helpers/ContextId";
import { getPagePayloadForChat } from "~/lib/helpers/pageBodyHelper";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatAssistantRequest {
  location: any;
  session_id: string;
  previous_context: ChatMessage[];
  user_query: string;
  persona?: any; // User's browsing persona
  existingUserDetails?: any; // User's existing details (name, email, etc.)
  country?: string; // Country/region for property search
  shortlistedProperties?: string[]; // Array of shortlisted property IDs
  bookingDateTime?: string; // Booking date and time
  recordData?: any; // Property data from the current page
  isPropertyExists?: boolean; // Whether matching properties exist after chat-driven area filters
  currentUrl?: string; // Current URL path without domain
  pageBody?: unknown; // Structured page context or a sanitized DOM snapshot
  selectedRequirements?: string[];
}

export type ChatResponseItem = {
  geolocation?: { lat: number | null; long: number | null } | null;
  htmlValue: string;
  images?: string[];
  textValue: string;
  title?: string;
  db_id?: string;
  website_url?: string;
  schemaVersion?: string;
  showCalendar?: boolean;
  metadata?: {
    lead_field?: string;
    step?: number;
  };
  items?: Array<{
    image: string;
    partnerLogo?: string;
    providerLogo?: string;
    title: string;
    description?: string;
    price: number;
    capacity?: string;
    location: string;
    website_url?: string;
    geolocation?: {
      lat: number;
      long: number;
    };
  }>;
  properties?: Array<{
    _id?: string | null;
    name: string;
    image: string;
    location: string;
    price: string; // e.g., "INR 14,000 per workstation per month"
    capacity?: string;
    geolocation?: {
      lat: number;
      long: number;
    };
    website_url?: string;
    providerLogo?: string | null;
    partnerLogo?: string | null;
  }>;
};

export interface ChatAssistantResponse {
  response: ChatResponseItem[];
  session_id: string;
  suggestions?: string[];
  multiSelect?: boolean;
  calendarTrigger?: boolean;
  db_id?: string | null;
  latency_ms?: number;
  lead_captured?: boolean;
  lead_id?: string | null;
  model?: string;
  propertyData?: any[]; // Property data at root level
  userSelectedLocation?: {
    lat?: number | null;
    long?: number | null;
    lng?: number | null;
  } | null;
  filteredParams?: Record<string, string | number | null | undefined>;
  navigateTo?: {
    section?: string | null;
    tag?: string | null;
    url?: string | null;
  } | null;
}

/** A host without a scheme ("ai-dev.office-hub.com") makes `${baseUrl}/api/...`
 * a RELATIVE url, so the call lands on the site itself
 * (https://dev-v2.office-hub.com/ai-dev.office-hub.com/api/assistant) instead of
 * the assistant. The env var is baked in at build time, so one bad value breaks
 * every request until the next deploy — normalise it here. */
function getAssistantBaseUrl(): string {
  const configured = (
    process.env.PUBLIC_REMIX_CHAT_ASSISTANT_BASE_URL || ""
  ).trim();
  if (!configured) return "http://127.0.0.1:8000";
  const withScheme = /^https?:\/\//i.test(configured)
    ? configured
    : `https://${configured}`;
  return withScheme.replace(/\/+$/, "");
}

function getAssistantBearerToken(): string | undefined {
  const t =
    process.env.PUBLIC_CHATBOT_KEY || import.meta.env.PUBLIC_CHATBOT_KEY;
  return t && t.trim().length > 0 ? t.trim() : undefined;
}

function getCurrentUrlPath(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}`;
}

class ChatAssistantService {
  private baseUrl: string;
  private headers: HeadersInit;

  constructor() {
    this.baseUrl = getAssistantBaseUrl();
    this.headers = {
      Accept: "application/json",
      "ngrok-skip-browser-warning": "true",
    };

    const token = getAssistantBearerToken();
    if (token) {
      this.headers = {
        ...this.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  }

  private async handleResponse(response: Response) {
    const ctxId = readContextIdFromResponse(response);
    if (ctxId) setContextId(ctxId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("ChatAssistantService.handleResponse:error", {
        status: response.status,
        statusText: response.statusText,
        errorData,
      });
      throw new Error(
        response.statusText || "Failed to get response from chat assistant",
      );
    }

    return response.json();
  }

  async sendMessage(
    sessionId: string,
    previousContext: ChatMessage[],
    userQuery: string,
    persona?: any,
    userDetails?: any,
    location?: any,
    shortlistedProperties?: string[],
    bookingDateTime?: string,
    recordData?: any,
    isPropertyExists?: boolean,
    selectedRequirements?: string[],
  ): Promise<ChatAssistantResponse> {
    const headers: Record<string, string> = {
      ...(this.headers as Record<string, string>),
    };
    const ctx = ensureContextId();
    if (ctx) headers["X-Context-Id"] = ctx;

    headers["Content-Type"] = "application/json";

    const requestBody: ChatAssistantRequest = {
      session_id: sessionId,
      previous_context: previousContext,
      user_query: userQuery,
      location,
    };

    if (persona) requestBody.persona = persona;
    if (userDetails) requestBody.existingUserDetails = userDetails;
    if (location) requestBody.location = location;
    if (shortlistedProperties && shortlistedProperties.length > 0) {
      requestBody.shortlistedProperties = shortlistedProperties;
    }
    if (bookingDateTime) {
      requestBody.bookingDateTime = bookingDateTime;
    }
    if (recordData) {
      requestBody.recordData = recordData;
    }
    if (isPropertyExists !== undefined) {
      requestBody.isPropertyExists = isPropertyExists;
    }
    if (selectedRequirements) {
      requestBody.selectedRequirements = selectedRequirements;
    }
    const currentUrl = getCurrentUrlPath();
    if (currentUrl) {
      requestBody.currentUrl = currentUrl;
      requestBody.pageBody = getPagePayloadForChat(currentUrl);
    }

    const response = await fetch(`${this.baseUrl}/api/assistant`, {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
      mode: "cors",
    });

    return this.handleResponse(response);
  }

  async submitFeedback(queryFeedback: {
    User: string;
    Assistant: string;
    Feedback: boolean;
  }): Promise<{ success: boolean; message?: string; error?: string }> {
    const headers: Record<string, string> = {
      ...(this.headers as Record<string, string>),
    };
    const ctx = ensureContextId();
    if (ctx) headers["X-Context-Id"] = ctx;

    headers["Content-Type"] = "application/json";
    headers["accept"] = "application/json";

    try {
      const response = await fetch(`${this.baseUrl}/api/feedback`, {
        method: "POST",
        headers,
        body: JSON.stringify(queryFeedback),
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("ChatAssistantService.submitFeedback:error", {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        return {
          success: false,
          error: response.statusText || "Failed to submit feedback",
        };
      }

      const result = await response.json();
      return {
        success: true,
        message: result.message || "Feedback submitted successfully",
      };
    } catch (error) {
      console.error("ChatAssistantService.submitFeedback:exception", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}

export default new ChatAssistantService();
